import React, { useState } from "react";
import { Link } from "react-router-dom";
import { FiHeart } from "react-icons/fi";

const BASE_URL = "https://pro-dash.orima.ma/";

const toNumber = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const ProductCard = ({ product }) => {
  const [liked, setLiked] = useState(false);

  const {
    id,
    main_image,
    name,
    selling_price,
    promotional_price,
    badge,
  } = product || {};

  const sell = toNumber(selling_price);
  const promo = toNumber(promotional_price);

  const hasDiscount = promo > 0 && sell > 0 && promo < sell;

  const discountPercent = hasDiscount
    ? Math.round(((sell - promo) / sell) * 100)
    : 0;

  const formatPrice = (price) => {
    const num = toNumber(price);
    if (!num) return "-";
    return `${num.toFixed(2)} MAD`;
  };

  const imageUrl = main_image
    ? (main_image.startsWith("http")
        ? main_image
        : `${BASE_URL}${String(main_image).replace(/^\/+/, "")}`)
    : "https://via.placeholder.com/400x500?text=No+Image";

  return (
    <div className="group relative w-full">
      <Link to={`/product/${id}`} className="block">
        {/* Card container */}
        <div className="rounded-2xl border border-gray-200 bg-white shadow-sm transition-all duration-300 group-hover:shadow-lg group-hover:border-gray-900">
          {/* Image */}
          <div className="relative overflow-hidden rounded-t-2xl bg-gray-50 aspect-[3/4]">
            <img
              src={imageUrl}
              alt={name || "Produit"}
              className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
              onError={(e) => {
                e.target.src = "https://via.placeholder.com/400x500?text=No+Image";
              }}
            />

            {/* soft overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

            {/* Discount badge */}
            {hasDiscount && (
              <div className="absolute top-3 left-3 z-10">
                <div className="rounded-full bg-gray-900 px-3 py-1 text-xs font-bold text-white">
                  -{discountPercent}%
                </div>
              </div>
            )}

            {/* Custom badge (only if no discount) */}
            {badge && !hasDiscount && (
              <div className="absolute top-3 left-3 z-10">
                <div className="rounded-full bg-white/95 backdrop-blur px-3 py-1 text-xs font-semibold text-gray-900 border border-gray-200">
                  {badge}
                </div>
              </div>
            )}

            {/* Like button */}
            <button
              onClick={(e) => {
                e.preventDefault();
                setLiked((v) => !v);
              }}
              className={`absolute top-3 right-3 z-10 h-10 w-10 rounded-full border bg-white/95 backdrop-blur flex items-center justify-center transition-all duration-300 ${
                liked
                  ? "border-gray-900 text-gray-900"
                  : "border-gray-200 text-gray-600 hover:border-gray-900 hover:text-gray-900"
              }`}
              aria-label="Ajouter aux favoris"
              title="Favori"
            >
              <FiHeart className={`h-4 w-4 ${liked ? "fill-current" : ""}`} />
            </button>

            {/* Yellow hint bar on hover (to match new design) */}
            <div className="absolute bottom-0 left-0 right-0 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
              <div className="bg-yellow-200 text-gray-900 text-center font-extrabold text-sm py-2">
                Voir le produit
              </div>
            </div>
          </div>

          {/* Body */}
          <div className="p-3 sm:p-4">
            <h3 className="text-sm sm:text-[15px] font-semibold text-gray-900 leading-snug line-clamp-2 min-h-[2.5rem]">
              {name}
            </h3>

            <div className="mt-2 flex items-baseline gap-2">
              {hasDiscount ? (
                <>
                  <span className="text-base font-extrabold text-gray-900">
                    {formatPrice(promo)}
                  </span>
                  <span className="text-sm line-through text-gray-400">
                    {formatPrice(sell)}
                  </span>
                </>
              ) : (
                <span className="text-base font-extrabold text-gray-900">
                  {formatPrice(sell)}
                </span>
              )}
            </div>

            {/* Small underline animation (desktop) */}
            <div className="mt-3 hidden sm:block">
              <div className="h-px bg-gray-200 scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />
            </div>
          </div>
        </div>
      </Link>
    </div>
  );
};

export default ProductCard;
